/*
 * Ce qui aide les dépôts à lire beaucoup sans multiplier les requêtes. Pur :
 * ni réseau ni client Supabase.
 */

/** Les éléments dans l'ordre de `ids`, sans les absents ni les doublons. */
export function selonIds<T extends { id: string }>(
  ids: readonly string[],
  elements: readonly T[]
): T[] {
  const parId = new Map(elements.map((e) => [e.id, e]));
  const vus = new Set<string>();
  const rendus: T[] = [];
  for (const id of ids) {
    const element = parId.get(id);
    if (element === undefined || vus.has(id)) continue;
    vus.add(id);
    rendus.push(element);
  }
  return rendus;
}

/**
 * Des paquets d'au plus `taille` identifiants : une liste `in.(…)` trop longue
 * dépasse ce qu'une adresse peut porter.
 */
export function parLots<T>(elements: readonly T[], taille: number): T[][] {
  const lots: T[][] = [];
  for (let i = 0; i < elements.length; i += taille) lots.push(elements.slice(i, i + taille));
  return lots;
}

type Methode = (...args: never[]) => Promise<unknown>;

/** Les noms des méthodes d'un dépôt qui rendent une promesse. */
export type MethodesDe<D> = {
  [K in keyof D]: D[K] extends Methode ? K : never;
}[keyof D] &
  string;

/** Les lectures en vol, partagées par tous les dépôts enveloppés ensemble. */
export type EnVol = Map<string, Promise<unknown>>;

export interface OptionsPartage<D> {
  /** Les lectures à mettre en commun. */
  lectures: readonly MethodesDe<D>[];
  /** Les autres lectures, qui ne changent rien et n'oublient donc rien. */
  neutres?: readonly MethodesDe<D>[];
  /** Le préfixe des clés : le nom du dépôt, quand plusieurs partagent `enVol`. */
  nom?: string;
  enVol?: EnVol;
}

/* `lister()` et `lister(undefined)` demandent la même chose. */
function cleDe(nom: string, methode: string, args: readonly unknown[]): string {
  let fin = args.length;
  while (fin > 0 && args[fin - 1] === undefined) fin--;
  return `${nom}.${methode}:${JSON.stringify(args.slice(0, fin))}`;
}

/**
 * Le dépôt, dont deux appels identiques d'une même lecture, lancés pendant
 * qu'elle est en vol, partagent la même requête. Rien n'est gardé une fois la
 * réponse arrivée : ce n'est pas un cache.
 *
 * Chaque appelant reçoit sa copie : un écran qui trie la liste en place ne
 * dérange pas son voisin. Toute autre méthode (une écriture) oublie les lectures
 * en vol, à son départ et à son retour : un écran qui relit après avoir écrit
 * n'attrape pas une réponse partie avant l'écriture.
 */
export function partager<D extends object>(depot: D, options: OptionsPartage<D>): D {
  const enVol = options.enVol ?? new Map<string, Promise<unknown>>();
  const nom = options.nom ?? '';
  const lectures = new Set<string>(options.lectures);
  const neutres = new Set<string>(options.neutres ?? []);
  const enveloppe: Record<string, unknown> = {};

  for (const [methode, valeur] of Object.entries(depot)) {
    if (typeof valeur !== 'function') {
      enveloppe[methode] = valeur;
      continue;
    }
    /* Lue à chaque appel : la méthode d'origine peut avoir été remplacée depuis. */
    const appeler = (args: unknown[]) =>
      ((depot as Record<string, unknown>)[methode] as (...a: unknown[]) => Promise<unknown>).apply(
        depot,
        args
      );

    if (neutres.has(methode)) {
      enveloppe[methode] = (...args: unknown[]) => appeler(args);
    } else if (lectures.has(methode)) {
      enveloppe[methode] = (...args: unknown[]) => {
        const cle = cleDe(nom, methode, args);
        const deja = enVol.get(cle);
        if (deja !== undefined) return deja.then((r) => structuredClone(r));
        const promesse = appeler(args).finally(() => {
          if (enVol.get(cle) === promesse) enVol.delete(cle);
        });
        enVol.set(cle, promesse);
        return promesse;
      };
    } else {
      enveloppe[methode] = (...args: unknown[]) => {
        enVol.clear();
        return appeler(args).finally(() => {
          enVol.clear();
        });
      };
    }
  }
  return enveloppe as D;
}
